import { useEffect, useMemo, useState, type FormEvent } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, KeyRound, Search, UserPlus, XCircle } from "lucide-react";
import {
  APPROVAL_STATUS_LABELS,
  ROLES,
  canApproveUsers,
  canManageUsers,
  type ApprovalStatus,
  type CreateUserInput,
  type Role,
} from "@academichub/shared";
import { ApiError } from "../api/client";
import { listFaculties, listStudyPrograms, listUnits } from "../api/master";
import { approveUser, createUser, listUsers, rejectUser, type UserListStatus } from "../api/users";
import type { UserSummary } from "../api/types";
import { useAuth } from "../hooks/useAuth";
import { filterUnitsByProgram } from "../lib/activity";
import { formatDateTime, roleLabel } from "../lib/format";
import {
  buildCreateUserPayload,
  createdUserMessage,
  describeUserScope,
  emptyUserForm,
  generatePassword,
  roleOptions,
  scopeRequirements,
  type UserFormState,
} from "../lib/user";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Spinner,
  Textarea,
} from "../components/ui";

const PER_PAGE = 20;

const STATUS_FILTERS: Array<{ value: UserListStatus; label: string }> = [
  { value: "ALL", label: "Semua status" },
  { value: "PENDING", label: "Menunggu persetujuan" },
  { value: "APPROVED", label: "Aktif" },
  { value: "REJECTED", label: "Ditolak" },
];

function approvalTone(status: ApprovalStatus): "success" | "warning" | "danger" {
  if (status === "APPROVED") return "success";
  if (status === "REJECTED") return "danger";
  return "warning";
}

/** Halaman Kelola Pengguna: persetujuan pendaftaran, daftar akun, dan pembuatan akun. */
export function UserManagementPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const mayManage = canManageUsers(user?.role);
  const mayApprove = canApproveUsers(user?.role);
  const options = useMemo(() => roleOptions(user?.role), [user?.role]);

  const [form, setForm] = useState<UserFormState>(() =>
    emptyUserForm(options[0]?.value ?? "MAHASISWA"),
  );
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState<{ role: Role | ""; status: UserListStatus; q: string }>({
    role: "",
    status: "ALL",
    q: "",
  });
  const [page, setPage] = useState(1);
  const [rejectTarget, setRejectTarget] = useState<UserSummary | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [decisionError, setDecisionError] = useState<string | null>(null);

  const programsQuery = useQuery({
    queryKey: ["master", "study-programs"],
    queryFn: listStudyPrograms,
    enabled: mayManage,
  });
  const unitsQuery = useQuery({
    queryKey: ["master", "units"],
    queryFn: listUnits,
    enabled: mayManage,
  });
  const facultiesQuery = useQuery({
    queryKey: ["master", "faculties"],
    queryFn: listFaculties,
    enabled: mayManage,
  });

  const usersQuery = useQuery({
    queryKey: ["users", filters, page],
    queryFn: () =>
      listUsers({
        page,
        perPage: PER_PAGE,
        role: filters.role || undefined,
        status: filters.status,
        q: filters.q || undefined,
      }),
    enabled: mayManage,
    placeholderData: keepPreviousData,
  });

  const pendingQuery = useQuery({
    queryKey: ["users", "pending"],
    queryFn: () => listUsers({ status: "PENDING", perPage: 50 }),
    enabled: mayApprove,
  });

  // useMemo menjaga identitas referensi agar tidak memicu ulang useEffect di bawah.
  const programs = useMemo(() => programsQuery.data?.items ?? [], [programsQuery.data]);
  const units = useMemo(() => unitsQuery.data?.items ?? [], [unitsQuery.data]);
  const faculties = useMemo(() => facultiesQuery.data?.items ?? [], [facultiesQuery.data]);

  const requirements = scopeRequirements(form.role);
  const availableUnits = useMemo(
    () => filterUnitsByProgram(units, form.studyProgramId),
    [units, form.studyProgramId],
  );

  // Bila pembuat hanya berwenang atas satu program studi/fakultas/unit,
  // pilihannya diisi otomatis supaya formulir tidak menuntut pilihan semu.
  useEffect(() => {
    if (!requirements.needsStudyProgram || form.studyProgramId) return;
    const only = programs.length === 1 ? programs[0] : undefined;
    if (only) setForm((current) => ({ ...current, studyProgramId: only.id }));
  }, [requirements.needsStudyProgram, form.studyProgramId, programs]);

  useEffect(() => {
    if (!requirements.needsFaculty || form.facultyId) return;
    const only = faculties.length === 1 ? faculties[0] : undefined;
    if (only) setForm((current) => ({ ...current, facultyId: only.id }));
  }, [requirements.needsFaculty, form.facultyId, faculties]);

  useEffect(() => {
    if (!requirements.needsUnit || form.unitId) return;
    const only = availableUnits.length === 1 ? availableUnits[0] : undefined;
    if (only) setForm((current) => ({ ...current, unitId: only.id }));
  }, [requirements.needsUnit, form.unitId, availableUnits]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateUserInput) => createUser(payload),
    onSuccess: async (created) => {
      setError(null);
      setSuccess(createdUserMessage(created));
      setForm(emptyUserForm(form.role));
      setShowPassword(false);
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) => {
      setSuccess(null);
      setError(caught instanceof ApiError ? caught.message : "Akun pengguna gagal dibuat.");
    },
  });

  const approveMutation = useMutation({
    mutationFn: (userId: string) => approveUser(userId),
    onSuccess: async (updated) => {
      setDecisionError(null);
      setSuccess(
        `Pendaftaran ${updated.username} (${roleLabel(updated.role)}) disetujui dan akunnya sudah dapat dipakai.`,
      );
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) => {
      setDecisionError(
        caught instanceof ApiError ? caught.message : "Pendaftaran gagal disetujui.",
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (payload: { userId: string; reason: string }) =>
      rejectUser(payload.userId, payload.reason),
    onSuccess: async (updated) => {
      setDecisionError(null);
      setRejectTarget(null);
      setRejectReason("");
      setSuccess(`Pendaftaran ${updated.username} ditolak dan alasannya sudah dicatat.`);
      await queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (caught) => {
      setDecisionError(caught instanceof ApiError ? caught.message : "Pendaftaran gagal ditolak.");
    },
  });

  function update<K extends keyof UserFormState>(key: K, value: UserFormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function changeRole(role: Role): void {
    // Lingkup direset karena kewajibannya berbeda per peran.
    setForm((current) => ({
      ...current,
      role,
      facultyId: "",
      studyProgramId: "",
      unitId: "",
    }));
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    let payload: CreateUserInput;
    try {
      payload = buildCreateUserPayload(form);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Data akun belum lengkap.");
      return;
    }

    try {
      await createMutation.mutateAsync(payload);
    } catch {
      // Pesan galat sudah ditangani oleh onError mutation.
    }
  }

  if (!mayManage) {
    return (
      <div className="space-y-5">
        <PageHeader title="Kelola Pengguna" />
        <Alert tone="info">
          Menu ini hanya tersedia untuk Admin Program Studi, Admin Fakultas, dan Admin Institut.
        </Alert>
      </div>
    );
  }

  const items = usersQuery.data?.items ?? [];
  const pending = pendingQuery.data?.items ?? [];
  const lookups = { faculties, programs, units };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Kelola Pengguna"
        description="Setujui pendaftaran mandiri, buat akun dosen/mahasiswa/operator unit/admin, dan telusuri akun pada lingkup wewenang Anda."
      />

      {mayApprove && pending.length > 0 ? (
        <Card className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-semibold text-slate-800">
              <CheckCircle2 className="size-4" aria-hidden />
              Menunggu persetujuan ({pending.length})
            </h2>
            <p className="text-sm text-slate-600">
              Akun baru dapat login setelah Anda menyetujuinya.
            </p>
          </div>

          {decisionError ? <Alert tone="error">{decisionError}</Alert> : null}

          <ul className="divide-y divide-slate-200">
            {pending.map((item) => (
              <li
                key={item.id}
                className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{item.fullName}</p>
                  <p className="truncate text-xs text-slate-500">
                    {item.username} · {item.email}
                    {item.identifierNumber ? ` · ${item.identifierNumber}` : ""}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <Badge tone="info">{roleLabel(item.role)}</Badge>
                    <span>{describeUserScope(item, lookups)}</span>
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    onClick={() => {
                      setDecisionError(null);
                      setSuccess(null);
                      approveMutation.mutate(item.id);
                    }}
                    loading={approveMutation.isPending && approveMutation.variables === item.id}
                  >
                    <CheckCircle2 className="size-4" aria-hidden />
                    Setujui
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDecisionError(null);
                      setSuccess(null);
                      setRejectTarget(item);
                      setRejectReason("");
                    }}
                  >
                    <XCircle className="size-4" aria-hidden />
                    Tolak
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card className="space-y-4">
        <h2 className="flex items-center gap-2 font-semibold text-slate-800">
          <UserPlus className="size-4" aria-hidden />
          Buat akun baru
        </h2>

        <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void onSubmit(event)}>
          <Field label="Username" htmlFor="new-username" hint="Huruf, angka, titik, garis bawah.">
            <Input
              id="new-username"
              name="username"
              autoComplete="off"
              required
              minLength={3}
              maxLength={60}
              placeholder="mis. dosen.farmasi2"
              value={form.username}
              onChange={(event) => update("username", event.target.value)}
            />
          </Field>

          <Field label="Nama lengkap" htmlFor="new-full-name">
            <Input
              id="new-full-name"
              name="fullName"
              required
              minLength={3}
              maxLength={150}
              placeholder="mis. Dr. Anisa Putri, M.Farm."
              value={form.fullName}
              onChange={(event) => update("fullName", event.target.value)}
            />
          </Field>

          <Field label="Email" htmlFor="new-email">
            <Input
              id="new-email"
              name="email"
              type="email"
              required
              maxLength={150}
              placeholder="mis. anisa@istn.ac.id"
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </Field>

          <Field
            label="Nomor induk"
            htmlFor="new-identifier"
            hint="NIDN/NIM/NIP. Opsional, tetapi tidak boleh sama dengan akun lain."
          >
            <Input
              id="new-identifier"
              name="identifierNumber"
              maxLength={60}
              placeholder="mis. NIDN-0401019003"
              value={form.identifierNumber}
              onChange={(event) => update("identifierNumber", event.target.value)}
            />
          </Field>

          <Field label="Peran" htmlFor="new-role">
            <Select
              id="new-role"
              name="role"
              value={form.role}
              onChange={(event) => changeRole(event.target.value as Role)}
            >
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>

          {requirements.needsStudyProgram ? (
            <Field label="Program studi" htmlFor="new-study-program">
              <Select
                id="new-study-program"
                name="studyProgramId"
                required
                value={form.studyProgramId}
                onChange={(event) => {
                  update("studyProgramId", event.target.value);
                  update("unitId", "");
                }}
              >
                <option value="">Pilih program studi</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name} ({program.code})
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {requirements.needsUnit ? (
            <Field
              label="Unit"
              htmlFor="new-unit"
              hint="Unit harus berada di program studi tersebut."
            >
              <Select
                id="new-unit"
                name="unitId"
                required
                value={form.unitId}
                onChange={(event) => update("unitId", event.target.value)}
              >
                <option value="">Pilih unit</option>
                {availableUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {requirements.needsFaculty ? (
            <Field label="Fakultas" htmlFor="new-faculty">
              <Select
                id="new-faculty"
                name="facultyId"
                required
                value={form.facultyId}
                onChange={(event) => update("facultyId", event.target.value)}
              >
                <option value="">Pilih fakultas</option>
                {faculties.map((faculty) => (
                  <option key={faculty.id} value={faculty.id}>
                    {faculty.name} ({faculty.code})
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          <div className="sm:col-span-2">
            <Field
              label="Kata sandi awal"
              htmlFor="new-password"
              hint="Minimal 10 karakter serta memuat huruf besar, huruf kecil, dan angka."
            >
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="new-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  maxLength={200}
                  className="min-w-48 flex-1"
                  value={form.password}
                  onChange={(event) => update("password", event.target.value)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    update("password", generatePassword());
                    setShowPassword(true);
                  }}
                >
                  <KeyRound className="size-4" aria-hidden />
                  Buat acak
                </Button>
              </div>
            </Field>
            <label className="mt-2 flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                className="size-4 rounded border-slate-300"
                checked={showPassword}
                onChange={(event) => setShowPassword(event.target.checked)}
              />
              Tampilkan kata sandi
            </label>
          </div>

          {error ? (
            <div className="sm:col-span-2">
              <Alert tone="error">{error}</Alert>
            </div>
          ) : null}
          {success ? (
            <div className="sm:col-span-2">
              <Alert tone="success">{success}</Alert>
            </div>
          ) : null}

          <div className="sm:col-span-2">
            <Button type="submit" loading={createMutation.isPending}>
              <UserPlus className="size-4" aria-hidden />
              Buat akun
            </Button>
          </div>
        </form>
      </Card>

      <Card className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="font-semibold text-slate-800">Daftar pengguna</h2>
            <p className="mt-1 text-sm text-slate-600">
              Hanya akun pada lingkup wewenang Anda yang ditampilkan.
            </p>
          </div>
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters((current) => ({ ...current, q: searchDraft.trim() }));
              setPage(1);
            }}
          >
            <Field label="Peran" htmlFor="filter-role">
              <Select
                id="filter-role"
                value={filters.role}
                onChange={(event) => {
                  setFilters((current) => ({ ...current, role: event.target.value as Role | "" }));
                  setPage(1);
                }}
              >
                <option value="">Semua peran</option>
                {ROLES.map((role) => (
                  <option key={role} value={role}>
                    {roleLabel(role)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Status" htmlFor="filter-status">
              <Select
                id="filter-status"
                value={filters.status}
                onChange={(event) => {
                  setFilters((current) => ({
                    ...current,
                    status: event.target.value as UserListStatus,
                  }));
                  setPage(1);
                }}
              >
                {STATUS_FILTERS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Cari" htmlFor="filter-q">
              <Input
                id="filter-q"
                value={searchDraft}
                maxLength={120}
                placeholder="Nama, username, atau nomor induk"
                onChange={(event) => setSearchDraft(event.target.value)}
              />
            </Field>
            <Button type="submit" variant="secondary">
              <Search className="size-4" aria-hidden />
              Cari
            </Button>
          </form>
        </div>

        {usersQuery.isLoading ? <Spinner label="Memuat pengguna..." /> : null}

        {!usersQuery.isLoading && items.length === 0 ? (
          <EmptyState
            title="Belum ada pengguna yang cocok"
            description="Ubah filter pencarian atau buat akun baru melalui formulir di atas."
          />
        ) : null}

        {items.length > 0 ? (
          <ul className="divide-y divide-slate-200">
            {items.map((item) => (
              <li
                key={item.id}
                className="grid gap-1 py-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1.2fr)] sm:items-center sm:gap-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{item.fullName}</p>
                  <p className="truncate text-xs text-slate-500">
                    {item.username} · {item.email}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="info">{roleLabel(item.role)}</Badge>
                  <Badge tone={approvalTone(item.approvalStatus)}>
                    {APPROVAL_STATUS_LABELS[item.approvalStatus]}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500">
                  <p>{describeUserScope(item, lookups)}</p>
                  <p>Masuk terakhir: {formatDateTime(item.lastLoginAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        <Pagination
          page={usersQuery.data?.page ?? 1}
          totalPages={usersQuery.data?.totalPages ?? 1}
          onChange={setPage}
        />
      </Card>

      <Modal
        open={rejectTarget !== null}
        title="Tolak pendaftaran akun"
        onClose={() => {
          setRejectTarget(null);
          setRejectReason("");
          setDecisionError(null);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason("");
              }}
            >
              Batal
            </Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              onClick={() => {
                if (!rejectTarget) return;
                const reason = rejectReason.trim();
                if (reason.length < 5) {
                  setDecisionError("Alasan penolakan minimal 5 karakter.");
                  return;
                }
                setDecisionError(null);
                rejectMutation.mutate({ userId: rejectTarget.id, reason });
              }}
            >
              <XCircle className="size-4" aria-hidden />
              Tolak pendaftaran
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Pendaftaran <span className="font-medium text-slate-800">{rejectTarget?.username}</span>
            {rejectTarget ? ` (${roleLabel(rejectTarget.role)})` : ""} akan ditolak. Pemiliknya
            tidak dapat login sampai akun disetujui kemudian.
          </p>
          <Field label="Alasan penolakan" htmlFor="reject-reason" hint="Minimal 5 karakter.">
            <Textarea
              id="reject-reason"
              value={rejectReason}
              minLength={5}
              maxLength={500}
              placeholder="mis. Data nomor induk tidak sesuai dengan basis data program studi."
              onChange={(event) => setRejectReason(event.target.value)}
            />
          </Field>
          {decisionError ? <Alert tone="error">{decisionError}</Alert> : null}
        </div>
      </Modal>
    </div>
  );
}
